import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
  onConfirmDangerMessage,
  onConfirmInfoMessage,
  onConfirmWarningMessage,
  useParamsState,
  useValidationErrors,
} from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import {
  useSupplierOrderDetail,
  useSupplyOrderManagmentEdit,
  useSupplyOrderPreview,
  useUploadSupplierOrderInvoice,
} from '@/services'
import { Button, showToast, Text } from '@mercai/clever'

import { DrawerSupplier, GeneralInformation, ShowTotal } from '../../components'

import {
  generateSupplyOrderPayload,
  populateUpdateSupplyOrder,
  schemaPO,
} from './supplier-order-form.constants'

import {
  SaleOrderProps,
  SupplyChainLinkProps,
  SupplyOrderUpdateManagementPayload,
} from '@/types'
import {
  Documents,
  SelectSupplyOrderItems,
  SupplyOrderStatusSelect,
} from './components'

import { DrawerAdditionalProducts } from '../../components/drawer-additional-product'

import { SupplyOrderValues } from '../../types'

import styles from './supplier-order-form.module.css'

export const SupplierOrderForm = () => {
  const { t } = useTranslation('screenSupplierOrderManagementComponents')

  const [loading, setLoading] = useState(false)

  const [queryParams, setQueryParams] = useParamsState({
    form: '',
  })

  const handleDrawerClose = () => {
    setQueryParams({ ...queryParams, form: '' })
  }

  const [values, setValues] = useState<SupplyOrderValues>({
    currency: 'MXN',
  })

  const [initialValues, setInitialValues] = useState<SupplyOrderValues>({
    currency: 'MXN',
  })

  const [chainLinksToBeRemoved, setChainLinksToBeRemoved] = useState<
    SupplyChainLinkProps[]
  >([])

  const [chainLinkIdsMarkedForRemoval, setChainLinkIdsMarkedForRemoval] =
    useState<Set<string>>(new Set())

  const [saleOrdersForGeneration, setSaleOrdersForGeneration] = useState<
    Set<string>
  >(new Set())

  const affectedSaleOrders = useMemo(() => {
    const saleOrders = chainLinksToBeRemoved
      .map((link) => link.saleOrder)
      .filter(
        (saleOrder): saleOrder is SaleOrderProps => saleOrder !== undefined,
      )

    const uniqueSaleOrders = saleOrders.filter(
      (saleOrder, index, self) =>
        index === self.findIndex((s) => s.uuid === saleOrder.uuid),
    )

    return uniqueSaleOrders
  }, [chainLinksToBeRemoved])

  const purchaseOrderPreviewData = useMemo(() => {
    return {
      includeIva: values?.includeIva || false,
      items:
        values?.supplyOrderItems?.map((item) => ({
          quantity: item?.quantity || 0,
          cost: item?.cost ? +item?.cost : 0,
        })) || [],
    }
  }, [values?.currency, values?.supplyOrderItems, values?.includeIva])

  const { data: purchaseOrderPreview, isLoading: isLoadingPreviewPO } =
    useSupplyOrderPreview(
      purchaseOrderPreviewData,
      !!(values?.supplyOrderItems && values?.supplyOrderItems.length),
    )

  const { uuid } = useParams()

  const navigate = useNavigate()

  const { data: supplierOrderDetail } = useSupplierOrderDetail(uuid)

  const handleFileDrop = (
    files: File[],
    key: 'invoicePDFFile' | 'invoiceXMLFile',
  ) => {
    const file = files[0]
    if (file) {
      setValues((prev) => ({
        ...prev,
        [key]: file,
      }))
    }
  }

  const handleCancelEdit = async () => {
    const confirm = await onConfirmDangerMessage(
      t('generatePurchaseOrderCreateConfirmTitle'),
      t('generatePurchaseOrderCreateConfirmCancelMessage'),
      t('generatePurchaseOrderCreateConfirmButtonCreate'),
      t('generatePurchaseOrderCreateConfirmButtonCancel'),
    )

    if (!confirm) {
      return
    }

    return navigate(
      ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.fullPath(),
    )
  }

  const handleToggleSaleOrderForGeneration = (
    saleOrderUuid: string,
    include: boolean,
  ) => {
    setSaleOrdersForGeneration((prev) => {
      const newSet = new Set(prev)
      if (include) {
        newSet.add(saleOrderUuid)
      } else {
        newSet.delete(saleOrderUuid)
      }
      return newSet
    })
  }

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schemaPO(t),
    {
      items: values?.supplyOrderItems || [],
      supplierUuid: values?.supplier?.uuid,
    },
  )

  const { mutateAsync: updateSupplierOrder, isPending: isLoadingUpdatePO } =
    useSupplyOrderManagmentEdit(uuid)
  const { mutateAsync: uploadSupplierOrderInvoice } =
    useUploadSupplierOrderInvoice()

  const isUpdatingPurchaseOrder = isLoadingUpdatePO

  const performPurchaseOrderUpdate = async (): Promise<boolean> => {
    if (!isValid) {
      onHandleListenErrors(true)
      return false
    }

    onHandleListenErrors(false)
    setLoading(true)

    const payload: SupplyOrderUpdateManagementPayload = {
      supplyOrder: {
        uuid: supplierOrderDetail?.data?.uuid,
        ...generateSupplyOrderPayload(
          values,
          supplierOrderDetail?.data?.payments?.[0]?.uuid,
          supplierOrderDetail?.data?.supplyQuotation?.uuid,
        ),
      },
      supplyChainLinkUuid: chainLinksToBeRemoved
        .map((link) => link.uuid)
        .filter(Boolean) as string[],
    }

    try {
      const response = await updateSupplierOrder(payload)

      if (!response.success) {
        showToast({
          variant: 'error',
          title: response?.message
            ? response.message
            : t('generatePurchaseOrderUpdateErrorMessage'),
          position: 'bottom-right',
        })

        setLoading(false)
        return false
      }

      if (values?.invoicePDFFile && response.data?.invoiceUuid) {
        await uploadSupplierOrderInvoice({
          uuid: response.data.invoiceUuid,
          file: values.invoicePDFFile,
          type: 'pdf',
        })
      }

      if (values?.invoiceXMLFile && response.data?.invoiceUuid) {
        await uploadSupplierOrderInvoice({
          uuid: response.data.invoiceUuid,
          file: values.invoiceXMLFile,
          type: 'xml',
        })
      }

      setLoading(false)

      showToast({
        variant: 'success',
        title: t('generatePurchaseOrderUpdateSuccessMessage'),
        position: 'bottom-right',
      })

      return true
    } catch (error) {
      setLoading(false)
      showToast({
        variant: 'error',
        title: t('generatePurchaseOrderUpdateErrorMessage'),
        position: 'bottom-right',
      })
      return false
    }
  }

  const handleCreateNewSupplyOrder = async () => {
    const selectedSaleOrders = affectedSaleOrders.filter((saleOrder) =>
      saleOrdersForGeneration.has(saleOrder.uuid),
    )

    if (selectedSaleOrders?.length === 0) return

    const confirm = await onConfirmInfoMessage(
      t('generatePurchaseOrderCreateNewConfirmTitle'),
      t('generatePurchaseOrderCreateNewConfirmMessage'),
      t('generatePurchaseOrderCreateNewConfirmButtonCreate'),
      t('generatePurchaseOrderCreateNewConfirmButtonCancel'),
    )

    if (!confirm) {
      return
    }

    const updateSuccess = await performPurchaseOrderUpdate()

    if (!updateSuccess) {
      return
    }

    const references = selectedSaleOrders
      .map((saleOrder) => saleOrder.reference)
      .join(',')

    const supplierUuid = values?.supplier?.uuid

    navigate(
      `${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.GENERATE_PURCHASE_ORDER.CREATE.fullPath()}/?references=${references}&supplierUuid=${supplierUuid}`,
    )
  }

  const handlePurchaseOrderUpdate = async () => {
    const confirm = await onConfirmInfoMessage(
      t('generatePurchaseOrderSaveConfirmTitle'),
      t('generatePurchaseOrderSaveConfirmMessage'),
      t('generatePurchaseOrderSaveConfirmButtonSave'),
      t('generatePurchaseOrderSaveConfirmButtonCancel'),
    )

    if (!confirm) {
      return
    }

    const updateSuccess = await performPurchaseOrderUpdate()

    if (updateSuccess) {
      navigate(
        `${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.fullPath()}?search=${supplierOrderDetail?.data?.reference}`,
      )
    }
  }

  const handleSupplyOrderItemChange = (
    index: number,
    key: string,
    value: string | number | boolean | undefined,
  ) => {
    setValues((prev) => {
      const updatedItems = [...(prev.supplyOrderItems || [])]
      if (updatedItems[index]) {
        updatedItems[index] = {
          ...updatedItems[index],
          [key]: value,
        }
      }
      return { ...prev, supplyOrderItems: updatedItems }
    })
  }

  const handleSupplyOrderItemRemoval = async (index: number) => {
    const confirm = await onConfirmWarningMessage(
      t('generatePurchaseOrderDeleteItemConfirmTitle'),
      t('generatePurchaseOrderDeleteItemConfirmMessage'),
      t('generatePurchaseOrderDeleteItemConfirmButtonDelete'),
      t('generatePurchaseOrderDeleteItemConfirmButtonCancel'),
    )

    if (!confirm) {
      return
    }

    const itemToRemove = values?.supplyOrderItems?.[index]

    setValues((prev) => {
      const updatedItems = [...(prev.supplyOrderItems || [])]
      updatedItems.splice(index, 1)
      return { ...prev, supplyOrderItems: updatedItems }
    })

    if (itemToRemove?.supplyChainLink) {
      const relatedChainLinkIds = itemToRemove.supplyChainLink
        .map((link) => link.uuid)
        .filter(Boolean) as string[]

      setChainLinksToBeRemoved((prevRemoved) =>
        prevRemoved.filter(
          (link) => !relatedChainLinkIds.includes(link.uuid || ''),
        ),
      )

      setChainLinkIdsMarkedForRemoval((prev) => {
        const newSet = new Set(prev)
        relatedChainLinkIds.forEach((id) => newSet.delete(id))
        return newSet
      })
    }
  }

  const onRemoveChainLink = (itemIndex: number, linkIndex: number) => {
    setValues((prev) => {
      const updatedItems = [...(prev.supplyOrderItems || [])]
      const item = updatedItems[itemIndex]
      if (item && item.supplyChainLink && item.supplyChainLink[linkIndex]) {
        const removedLink = item.supplyChainLink[linkIndex]

        setChainLinksToBeRemoved((prevRemoved) => [...prevRemoved, removedLink])

        setChainLinkIdsMarkedForRemoval(
          (prev) => new Set([...prev, removedLink.uuid || '']),
        )

        return { ...prev, supplyOrderItems: updatedItems }
      }
      return prev
    })
  }

  const onRevertChainLink = (itemIndex: number, linkIndex: number) => {
    setValues((prev) => {
      const updatedItems = [...(prev.supplyOrderItems || [])]
      const item = updatedItems[itemIndex]
      if (item && item.supplyChainLink && item.supplyChainLink[linkIndex]) {
        const revertedLink = item.supplyChainLink[linkIndex]

        setChainLinksToBeRemoved((prevRemoved) =>
          prevRemoved.filter((link) => link.uuid !== revertedLink.uuid),
        )

        setChainLinkIdsMarkedForRemoval((prev) => {
          const newSet = new Set(prev)
          newSet.delete(revertedLink.uuid || '')
          return newSet
        })

        return { ...prev, supplyOrderItems: updatedItems }
      }
      return prev
    })
  }

  useEffect(() => {
    populateUpdateSupplyOrder(setValues, supplierOrderDetail?.data)
    populateUpdateSupplyOrder(setInitialValues, supplierOrderDetail?.data)
  }, [supplierOrderDetail])

  return (
    <div className={styles.core}>
      <header className={styles.header}>
        <Text variant="text-small" className={styles.breadcrumb}>
          <Link
            to={''}
            onClick={handleCancelEdit}
          >{`${t('generatePurchaseOrderBreadcum')}`}</Link>
          {` / `}
          <span>{supplierOrderDetail?.data?.reference}</span>
        </Text>

        <div className={styles['header-content']}>
          <div className={styles['header-title']}>
            <Text variant="title-large">
              {t('generatePurchaseOrderUpdateTitle')}
            </Text>

            <SupplyOrderStatusSelect
              values={values}
              setValues={setValues}
              initialStatus={initialValues?.status}
            />
          </div>

          <div className={styles['header-actions']}>
            <Button variant="secondary" onClick={handleCancelEdit}>
              {t('generatePurchaseOrderCancelButton')}
            </Button>
            <Button
              variant="primary"
              onClick={() => handlePurchaseOrderUpdate()}
              disabled={isUpdatingPurchaseOrder || loading}
              isActive={!isUpdatingPurchaseOrder && !loading}
              isLoading={isUpdatingPurchaseOrder || loading}
            >
              {t('generatePurchaseOrderConfirmButton')}
            </Button>
          </div>
        </div>
      </header>

      <GeneralInformation
        values={values}
        setValues={setValues}
        onShowAddSupplier={() =>
          setQueryParams({ ...queryParams, form: 'supplier' })
        }
        errors={errors}
        position={1}
      />

      <SelectSupplyOrderItems
        initialValues={initialValues}
        values={values}
        setValues={setValues}
        onRemove={handleSupplyOrderItemRemoval}
        onChangeValues={handleSupplyOrderItemChange}
        onRemoveChainLink={onRemoveChainLink}
        onRevertChainLink={onRevertChainLink}
        markedAsRemoved={chainLinkIdsMarkedForRemoval}
        disabled={!values?.supplier}
        onShowAddSaleOrders={() =>
          setQueryParams({
            ...queryParams,
            form: 'select-additional-products-form',
          })
        }
        preview={purchaseOrderPreview?.data}
        isLoading={isLoadingPreviewPO}
        errors={errors}
        position={2}
        affectedSaleOrders={affectedSaleOrders}
        saleOrdersForGeneration={saleOrdersForGeneration}
        onToggleSaleOrderForGeneration={handleToggleSaleOrderForGeneration}
        onCreateNewSupplyOrder={handleCreateNewSupplyOrder}
      />

      <ShowTotal
        total={purchaseOrderPreview?.data?.total || 0}
        currency={values?.currency || 'MXN'}
        subtotal={purchaseOrderPreview?.data?.totalWithoutIva || 0}
        iva={purchaseOrderPreview?.data?.iva || 0}
        dollarExchangeRate={purchaseOrderPreview?.data?.dollarExchangeRate || 0}
        isLoading={isLoadingPreviewPO}
        position={4}
      />

      <Documents
        values={values}
        setValues={setValues}
        onDropFile={handleFileDrop}
        position={5}
      />

      <DrawerSupplier
        supplier={values?.supplier}
        setSupplier={(sup) => setValues({ ...values, supplier: sup })}
        isOpen={queryParams.form === 'supplier'}
        onClose={handleDrawerClose}
      />

      <DrawerAdditionalProducts
        currency={values?.currency || 'MXN'}
        supplierUuid={values?.supplier?.uuid}
        additionalProducts={values?.supplyOrderItems}
        setAdditionalProducts={(val) =>
          setValues({ ...values, supplyOrderItems: val })
        }
        isOpen={queryParams.form === 'select-additional-products-form'}
        onClose={handleDrawerClose}
      />
    </div>
  )
}
